import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authState, userStorageKey } from '../../api';
import { ISection, loadBalancerAPI } from '../../api/load-balancer';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import SectionList from './components/SectionList/SectionList';
import SectionEditor from './components/SectionEditor/SectionEditor';
import './ContentManagementStyle.css';
import LanguageIcon from '@mui/icons-material/Language';

export function ContentManagement() {
  const [sections, setSections] = useState<ISection[]>([]);
  const [showSectionEditor, setShowSectionEditor] = useState<boolean>(false);
  const [editSection, setEditSection] = useState<ISection | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useRecoilState(authState);
  const [refreshedToken, setRefreshedToken] = useState(false);
  const authStateValue = useRecoilValue(authState);
  const [language, setLanguage] = useState('en');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [modalCompleteAndLoading, setModalCompleteAndLoading] = useState({
    modalsCompleted: false,
    isStatusLoading: true,
  });

  const {
    data: allLanguageData,
    isLoading: allLanguageLoading,
    error: allLanguageError,
  } = loadBalancerAPI.hookWrapperQuery.useGetAllLanguage();

  const validateLanguage = (lang: string): boolean => {
    return allLanguageData?.some((language) => language.code === lang) ?? false;
  };

  const handleChangeLanguage = async (event: any) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', newLanguage);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const langParam = urlParams.get('lang');
    if (langParam && validateLanguage(langParam)) {
      setLanguage(langParam);
    } else {
      if (!allLanguageLoading && !validateLanguage(langParam) && langParam) {
        setLanguage('en');
        setDialogMessage(`Not support language: ${langParam}`);
        setDialogOpen(true);
        navigate(`${location.pathname}?lang=en`);
      }
    }
  }, [allLanguageLoading, location.pathname, location.search, navigate]);

  const refreshToken = async () => {
    try {
      const auth = localStorage.getItem(userStorageKey);
      const parsedAuth = JSON.parse(auth || '{}');
      if (!parsedAuth.refreshToken) {
        throw new Error('No refresh token available');
      }

      const { data } = await loadBalancerAPI.apiInstance.refreshToken(parsedAuth.refreshToken);
      setAuth((prevState) => ({
        ...prevState,
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken,
        isLoading: false,
      }));
      setRefreshedToken(true);
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  };

  const fetchStatus = async () => {
    let isModalComplete: boolean;
    if (!authStateValue?.user?.id) return;
    try {
      const response = await loadBalancerAPI.apiInstance.getTwoFAState(authStateValue?.user?.id);
      if (response?.data) {
        const { twoFAState } = response.data;
        if (twoFAState === false) {
          isModalComplete = true;
          navigate('/content-management');
        } else {
          const statusResponse = await loadBalancerAPI.apiInstance.getTwoFactorStatus();
          const { otpEnabled, otpVerified, otpExpireAt } = statusResponse.data.data || {};

          if (otpEnabled) {
            const expireDate = new Date(otpExpireAt);
            const today = new Date();
            isModalComplete = otpVerified && expireDate > today;
          } else {
            isModalComplete = false;
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setModalCompleteAndLoading((prevState) => ({
        modalsCompleted: isModalComplete ?? prevState.modalsCompleted,
        isStatusLoading: false,
      }));
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem(userStorageKey);
    const checkAuthStatus = async () => {
      if (!auth) {
        navigate('/auth/login');
      } else if (authStateValue.user?.role === 0) {
        navigate('/home');
      } else {
        await fetchStatus();
      }
    };
    checkAuthStatus();
  }, [authStateValue?.user?.role, navigate]);

  useEffect(() => {
    if (!modalCompleteAndLoading.isStatusLoading && !modalCompleteAndLoading.modalsCompleted) {
      navigate('/two-factor-auth', { state: { redirectTo: '/content-management' } });
    }
  }, [modalCompleteAndLoading, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshToken();
      fetchStatus();
      if (!modalCompleteAndLoading.modalsCompleted) {
        navigate('/two-factor-auth', { state: { redirectTo: '/content-management' } });
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [modalCompleteAndLoading.modalsCompleted, navigate]);

  const {
    data: allSectionData,
    isLoading: isLoadingSectionData,
    error: errorSectionData,
    refetch: refetchAllSections,
  } = loadBalancerAPI.hookWrapperQuery.useGetAllSection({ enabled: modalCompleteAndLoading.modalsCompleted }, language);

  useEffect(() => {
    if (allSectionData) {
      setSections(allSectionData);
    }
  }, [allSectionData]);

  const handleEditSection = async (sectionId: string) => {
    try {
      const response = await loadBalancerAPI.apiInstance.getSectionById(sectionId);
      setEditSection(response.data.data);
      setShowSectionEditor(true);
    } catch (error) {
      console.error('Failed to fetch section:', error);
    }
  };

  const handleSaveNewSection = (newSection: ISection) => {
    if (editSection) {
      setSections((prevSections) =>
        prevSections.map((section) => (section.id === newSection.id ? newSection : section))
      );
    } else {
      setSections((prevSections) => [...prevSections, newSection]);
    }

    refetchAllSections();
    setShowSectionEditor(false);
    setEditSection(null);
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const prePosition = sections[index].position;
    const nextPosition = sections[index - 1].position;
    try {
      const response = await loadBalancerAPI.apiInstance.swapPosition(prePosition, nextPosition, language);
      if (response.status) {
        const newList = [...sections];
        [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
        setSections(newList);
        refetchAllSections();
      } else {
        console.error('Failed to swap positions');
      }
    } catch (error) {
      console.error('Error swapping positions:', error);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === sections.length - 1) return;
    const prePosition = sections[index].position;
    const nextPosition = sections[index + 1].position;
    try {
      const response = await loadBalancerAPI.apiInstance.swapPosition(prePosition, nextPosition, language);
      if (response.status) {
        const newList = [...sections];
        [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
        setSections(newList);
        refetchAllSections();
      } else {
        console.error('Failed to swap positions');
      }
    } catch (error) {
      console.error('Error swapping positions:', error);
    }
  };

  const handleTogglePublish = async (index: number) => {
    const sectionId = sections[index].id;
    const sectionToToggle = sections[index];
    try {
      if (sectionToToggle.isPublish) {
        const response = await loadBalancerAPI.apiInstance.offlineSection(sectionId);
        if (response.status) {
          const newList = [...sections];
          newList[index].isPublish = false;
          setSections(newList);
          refetchAllSections();
        } else {
          console.error('Failed to take section offline');
        }
      } else {
        const response = await loadBalancerAPI.apiInstance.publishSection(sectionId);
        if (response.status) {
          const newList = [...sections];
          newList[index].isPublish = true;
          setSections(newList);
          refetchAllSections();
        } else {
          console.error('Failed to publish section');
        }
      }
    } catch (error) {
      console.error('Error toggling section publish state:', error);
    }
  };

  const handleDelete = async (index: number) => {
    const sectionId = sections[index].id;
    const fileNameToDelete = sections[index].contentFile;
    try {
      const SectionDelRes = await loadBalancerAPI.apiInstance.deleteSection(sectionId);
      const MdFileDelRes = await loadBalancerAPI.apiInstance.deleteMdFile(language, fileNameToDelete);
      if (SectionDelRes.status && MdFileDelRes.status) {
        const newList = sections.filter((_, i) => i !== index);
        setSections(newList);
        refetchAllSections();
      } else {
        console.error('Failed to delete section');
      }
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  const handleCancel = () => {
    setShowSectionEditor(false);
    setEditSection(null);
  };

  if (modalCompleteAndLoading.isStatusLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!modalCompleteAndLoading.modalsCompleted) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Quickstart Guide Editor"
        action={
          !showSectionEditor &&
          (allLanguageError ? (
            <Typography color="error">Failed to load language</Typography>
          ) : allLanguageLoading ? (
            <CircularProgress />
          ) : (
            <FormControl>
              <Select
                labelId="language-select-label"
                value={language}
                onChange={handleChangeLanguage}
                input={
                  <OutlinedInput
                    startAdornment={
                      <InputAdornment position="start">
                        <LanguageIcon />
                      </InputAdornment>
                    }
                  />
                }
                sx={{
                  minWidth: 120,
                  '.MuiSelect-select': {
                    padding: '5px 14px',
                    fontSize: '16px',
                    fontWeight: 500,
                  },
                }}
              >
                {allLanguageData?.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))
        }
      />
      <CardContent sx={{ overflow: 'hidden' }}>
        {!showSectionEditor ? (
          <>
            <div className="add-new">
              Add new content section:
              <Button
                variant="contained"
                sx={{ ml: 1, backgroundColor: '#53b9d8 !important' }}
                onClick={() => setShowSectionEditor(true)}
              >
                Add
              </Button>
            </div>
            <SectionList
              language={language}
              sections={sections}
              isLoading={isLoadingSectionData}
              error={errorSectionData}
              onEdit={handleEditSection}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onTogglePublish={handleTogglePublish}
              onDelete={handleDelete}
            />
          </>
        ) : (
          <SectionEditor
            language={language}
            onSave={handleSaveNewSection}
            onCancel={handleCancel}
            section={editSection}
          />
        )}
      </CardContent>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setDialogOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default ContentManagement;
